import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { soldApi } from "../../lib/api";
import { Trash2, RotateCcw } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../../components/ui/toaster";

export default function AdminSold() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    soldApi.list().then(setItems).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Verkochte auto verwijderen?")) return;
    await soldApi.remove(id);
    toast({ title: "Verwijderd" });
    load();
  };

  const handleRestore = async (id) => {
    if (!window.confirm("Deze auto terug in stock zetten? De wagen wordt opnieuw zichtbaar in het publieke aanbod.")) return;
    setBusyId(id);
    try {
      const res = await soldApi.restore(id);
      toast({
        title: "Terug in stock",
        description: res.had_snapshot
          ? "Volledige listing hersteld."
          : "Wagen aangemaakt met basisinfo — vul de ontbrekende velden aan.",
      });
      // Open the edit form so the admin can verify/complete the listing
      navigate(`/admin/cars/${res.car_id}/edit`);
    } catch (e) {
      toast({ title: "Fout", description: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-8">
      <Toaster />
      <h1 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
        Verkochte <span className="text-[#c5a76f]">wagens</span>
      </h1>
      <p className="text-white/50 text-sm mb-7">{items.length} wagens verkocht</p>

      {loading ? (
        <div className="text-white/50">Laden...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((s) => (
            <div key={s.id} className="group bg-[#242424] border border-white/5 rounded-md overflow-hidden">
              <div className="relative aspect-[4/3] bg-[#1a1a1a]">
                {s.image && <img src={s.image} alt={s.title} className="w-full h-full object-cover grayscale" />}
                <div className="absolute top-3 right-3 bg-red-600/90 text-white text-xs font-bold tracking-widest px-3 py-1 rounded">
                  VERKOCHT
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-white text-base font-semibold">{s.title}</h3>
                <div className="text-xs text-white/50 mt-1">{s.year} · {s.km?.toLocaleString()} km · {s.fuel}</div>
                <div className="text-xs text-white/40 mt-2">Verkocht: {new Date(s.sold_at).toLocaleDateString("nl-BE")}</div>
                <div className="mt-4 flex items-center gap-4">
                  <button
                    onClick={() => handleRestore(s.id)}
                    disabled={busyId === s.id}
                    data-testid={`sold-restore-${s.id}`}
                    className="flex items-center gap-2 text-xs text-[#c5a76f] hover:text-[#d8bd86] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> {busyId === s.id ? "Bezig..." : "Terug in stock"}
                  </button>
                  <button
                    onClick={() => handleDelete(s.id)}
                    data-testid={`sold-delete-${s.id}`}
                    className="flex items-center gap-2 text-xs text-white/60 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Verwijderen
                  </button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="col-span-full text-center py-10 text-white/40">Nog geen verkochte wagens.</div>}
        </div>
      )}
    </div>
  );
}
